type Callback = (data?: any, callback?: any) => any;

export interface ISharedWorker {
  /**
   * 监听WorkerService的事件
   * @param eventName 事件名
   * @param callback 回调函数
   */
  on(eventName: string, callback: Callback): void;
  /**
   * 取消监听WorkerService的事件
   * @param eventName  事件名
   */
  off(eventName: string): void;
  /**
   * 触发WorkerService的监听
   * @param eventName 事件名
   * @param data 数据
   * @param callback 回调函数
   */
  emit(eventName: string, data: object, callback?: Callback): void;
}

export interface IWorker {
  /**
   * 创建SharedWorker实例，仅主线程可用
   * @param bundleName worker文件的bundleName
   */
  sharedWorker(bundleName: string): ISharedWorker;
  /**
   * 监听 主线程 触发的事件
   * 仅worker线程可用
   * @param eventName 事件名
   * @param callback 回调函数
   */
  on(eventName: string, callback: Callback): void;
  /**
   * 取消监听 主线程 触发的事件
   * 仅worker线程可用
   * @param eventName 事件名
   */
  off(eventName: string): void;
  /**
   * 触发 主线程 监听的事件
   * 仅worker线程可用
   * @param eventName
   * @param data 数据
   */
  emit(eventName: string, data: object): void;
}

const errorFn = () => {
  throw new Error('请在正确的线程中使用Worker');
};

/**
 * NativeWorker 和 NativeWorkerService 接口实现
 * NativeWorker 用于主线程
 * NativeWorkerService 用于子线程
 */
const genUUid = () => `${Date.now()}_${String(Math.random()).slice(-8)}`;

// 子线程 WorkerService
const WORKER_CALLBACK_MAP = {};
const MESSAGE_PROCESSED_SET = new Set(); // 防重复处理的消息ID集合

/**
 * 负责管理所有 worker 实例和消息路由
 */
class GlobalWorkerManager {
  private static instance: GlobalWorkerManager;
  private workerInstances: Map<string, NativeWorker> = new Map();
  private messageQueue: any[] = [];

  static getInstance(): GlobalWorkerManager {
    if (!GlobalWorkerManager.instance) {
      GlobalWorkerManager.instance = new GlobalWorkerManager();
    }
    return GlobalWorkerManager.instance;
  }

  /**
   * 注册 worker 实例
   */
  registerWorker(workerId: string, worker: NativeWorker): void {
    this.workerInstances.set(workerId, worker);
    console.log(`GlobalWorkerManager ${workerId} registered`);
  }

  /**
   * 注销 worker 实例
   */
  unregisterWorker(workerId: string): void {
    this.workerInstances.delete(workerId);
    console.log(`GlobalWorkerManager ${workerId} unregistered`);
  }

  /**
   * 获取 worker 实例
   */
  getWorker(workerId: string): NativeWorker | undefined {
    return this.workerInstances.get(workerId);
  }

  /**
   * 处理来自 worker 的消息并路由到对应的主线程实例
   */
  routeWorkerMessage(workerId: string, message: any): void {
    const worker = this.getWorker(workerId);
    if (worker) {
      worker.handleWorkerMessage(message);
    } else {
      console.warn(`GlobalWorkerManager ${workerId} not found for message routing`);
    }
  }

  /**
   * 向指定 worker 发送消息
   */
  sendMessageToWorker(workerId: string, message: any): void {
    // 模拟原生接口调用
    console.log(`Sending message to worker ${workerId}:`, message);

    // 在实际实现中，这里会调用原生接口
    // 现在模拟直接调用 worker 的消息处理
    setTimeout(() => {
      // if (globalThis.NativeWorkerService) {
      //   globalThis.NativeWorkerService.__on__(message);
      // }

      // @ts-ignore
      if (window.top.NativeWorkerService) {
        // @ts-ignore
        window.top.NativeWorkerService.__on__(message);
      }
    }, 0);
  }

  /**
   * 处理来自 worker 的事件订阅
   */
  subscribeWorkerEvent(workerId: string, eventName: string): void {
    console.log(`Subscribing to event ${eventName} for worker ${workerId}`);
    // 在实际实现中，这里会调用原生接口注册事件监听
  }

  /**
   * 处理来自 worker 的事件取消订阅
   */
  unsubscribeWorkerEvent(workerId: string, eventName: string): void {
    console.log(`Unsubscribing from event ${eventName} for worker ${workerId}`);
    // 在实际实现中，这里会调用原生接口取消事件监听
  }

  /**
   * 处理从 worker 发送到主线程的消息
   */
  handleWorkerToMainMessage(workerId: string, eventName: string, data: any): void {
    const worker = this.getWorker(workerId);
    if (worker) {
      worker.__on__(eventName, data);
    }
  }

  /**
   * 处理从 worker 调用主线程的回调
   */
  invokeMainThreadCallback(callbackData: any): void {
    const { worker_id } = callbackData;
    const worker = this.getWorker(worker_id);
    if (worker) {
      worker.__invokeCallBack__(callbackData);
    }
  }
}

/**
 * NativeWorker 类 - 主线程使用
 * 线程通信
 */
export class NativeWorker {
  private eventHandlers: Record<string, Function> = {};
  private workerId: string;
  private bundleName: string;
  private callbackMap: Record<string, Function> = {};

  /**
   * 创建 NativeWorker 实例
   * @param workerId worker 唯一标识
   * @param bundleName worker 文件的 bundleName
   */
  constructor(workerId: string, bundleName: string) {
    this.workerId = workerId;
    this.bundleName = bundleName;
    // 初始化 worker
    this._initWorker();
  }

  /**
   * 初始化 worker
   * 实际实现中可能需要与原生层交互创建 worker
   */
  private _initWorker(): void {
    // 实际实现可能需要调用原生接口创建 worker
    console.log(`NativeWorker ${this.workerId} with bundle ${this.bundleName} initialized`);

    // 注册当前 worker 实例到全局管理器
    const globalManager = GlobalWorkerManager.getInstance();
    globalManager.registerWorker(this.workerId, this);

    // 在实际实现中，这里可能需要注册一个全局回调，用于接收来自 worker 的消息
    // 例如：registerWorkerMessageHandler(this.workerId, this._handleWorkerMessage.bind(this));
  }

  /**
   * 处理来自 worker 的消息 (内部方法)
   * @param message 消息对象
   */
  private _handleWorkerMessage(message: any): void {
    const { type, eventName, data } = message;

    if (type === 'event' && eventName && this.eventHandlers[eventName]) {
      // 处理事件消息
      this.eventHandlers[eventName](data);
    } else if (type === 'callback' && message.callbackId && this.callbackMap[message.callbackId]) {
      // 处理回调消息
      this.callbackMap[message.callbackId](data);
      delete this.callbackMap[message.callbackId];
    }
  }

  /**
   * 处理来自 worker 的消息 (公有方法，供全局管理器调用)
   * @param message 消息对象
   */
  handleWorkerMessage(message: any): void {
    this._handleWorkerMessage(message);
  }

  /**
   * 订阅 worker 事件
   * @param eventName 事件名称
   */
  __subscribe__(eventName: string): void {
    // 实际实现可能需要调用原生接口订阅事件
    console.log(`Subscribed to event: ${eventName}`);

    // 在实际实现中，这里可能需要向原生层注册事件订阅
    // 例如：nativeSubscribeWorkerEvent(this.workerId, eventName);
    const globalManager = GlobalWorkerManager.getInstance();
    globalManager.subscribeWorkerEvent(this.workerId, eventName);
  }

  /**
   * 取消订阅 worker 事件
   * @param eventName 事件名称
   */
  __unsubscribe__(eventName: string): void {
    // 实际实现可能需要调用原生接口取消订阅事件
    console.log(`Unsubscribed from event: ${eventName}`);

    // 在实际实现中，这里可能需要向原生层取消事件订阅
    // 例如：nativeUnsubscribeWorkerEvent(this.workerId, eventName);
    const globalManager = GlobalWorkerManager.getInstance();
    globalManager.unsubscribeWorkerEvent(this.workerId, eventName);
  }

  /**
   * 向 worker 发送消息
   * @param data 消息数据
   */
  __emit__(data: any): void {
    // 实际实现可能需要调用原生接口发送消息
    console.log(`Emitting message to worker: ${JSON.stringify(data)}`);

    // 在实际实现中，这里可能需要调用原生接口发送消息到 worker
    // 例如：nativeSendMessageToWorker(this.workerId, data);

    // 使用全局管理器发送消息到 worker
    const globalManager = GlobalWorkerManager.getInstance();
    globalManager.sendMessageToWorker(this.workerId, data);

    // 注意：这里不再重复调用，因为 globalManager.sendMessageToWorker 已经处理了消息发送
    // 避免重复执行导致 worker 端处理函数被调用两次
  }

  /**
   * 接收 worker 发来的消息
   * @param eventName 事件名称
   * @param data 消息数据
   */
  __on__(eventName: string, data: any): void {
    // 由原生层调用，转发 worker 消息到主线程
    if (this.eventHandlers[eventName]) {
      this.eventHandlers[eventName](data);
    }
  }

  /**
   * 处理 worker 回调
   * @param callbackData 回调数据
   */
  __invokeCallBack__(callbackData: any): void {
    const { callback_id, data } = callbackData;
    if (this.callbackMap[callback_id]) {
      this.callbackMap[callback_id](data);
      delete this.callbackMap[callback_id];
    }
  }
}

// 主线程
class SharedWorker extends NativeWorker {
  static sharedWorkerInstanceMap = new Map();

  [x: string]: any;
  worker_id: string;
  worker_service_id: string;

  private listenCallbackMap = {};
  private emitCallbackMap = {};

  constructor(bundleName: string) {
    const workerId = `worker_${genUUid()}`;
    super(workerId, bundleName);
    this.worker_id = workerId;
    this.worker_service_id = bundleName;
  }

  /**
   * 监听WorkerService的事件
   */
  public on(eventName: string, callback: Callback) {
    this.listenCallbackMap[eventName] = callback;
    this.__subscribe__(eventName);
  }
  /**
   * 取消监听WorkerService的事件
   */
  public off(eventName) {
    delete this.listenCallbackMap[eventName];
    this.__unsubscribe__(eventName);
  }

  /**
   * 根据eventName获取callback，执行callback，参数是data
   * 用于回调 on 的监听
   * @param eventName
   * @param data
   */
  __on__(eventName: string, data: any) {
    // msg, data: any
    if (this.listenCallbackMap[eventName]) {
      this.listenCallbackMap[eventName](data);
    }
  }

  /**
   * 触发WorkerService的监听
   */
  public emit(eventName: string, data: object, callback: Callback) {
    let callback_id: string = '';
    if (callback) {
      callback_id = `callback_${genUUid()}`;
      this.emitCallbackMap[callback_id] = callback;
    }
    this.__emit__({
      msg: eventName,
      data,
      callback_id,
      worker_id: this.worker_id,
      worker_service_id: this.worker_service_id,
    });
  }

  /**
   * 用于接收emit的回调，JS SDK负责分发给业务， data里面有callback_id
   * JS  SDK 创建
   * @param invokeData
   */
  __invokeCallBack__(invokeData: any) {
    const { callback_id, data } = invokeData;
    if (this.emitCallbackMap[callback_id]) {
      this.emitCallbackMap[callback_id](data);
      delete this.emitCallbackMap[callback_id];
    }
  }
}

/**
 * 负责与主线程通信
 */
export const NativeWorkerService = {
  /**
   * 接收主线程发来的消息
   * @param data 消息数据
   */
  __on__(data: any): void {
    // 实际实现由 NativeWorkerService.on 注册的回调处理
    console.log(`NativeWorkerService received message: ${JSON.stringify(data)}`);

    // 在实际实现中，这里应该是由原生层调用，将消息转发给 JS
    // Worker.on 注册的回调会在 Worker.ts 中处理这个消息
  },

  /**
   * 向主线程发送消息
   * @param eventName 事件名称
   * @param data 消息数据
   */
  __emit__(eventName: string, data: any): boolean {
    // 实际实现可能需要调用原生接口发送消息到主线程
    console.log(`NativeWorkerService emitting message to main thread: ${eventName}, ${JSON.stringify(data)}`);

    // 在实际实现中，这里可能需要调用原生接口发送消息到主线程
    // 例如：nativeSendMessageToMainThread(eventName, data);

    return true;
  },

  /**
   * 调用主线程回调
   * @param callbackData 回调数据
   */
  __serviceInvokeCallback__(callbackData: any): void {
    // 实际实现可能需要调用原生接口调用主线程回调
    console.log(`NativeWorkerService invoking callback: ${JSON.stringify(callbackData)}`);

    // 在实际实现中，这里可能需要调用原生接口调用主线程回调
    // 例如：nativeInvokeMainThreadCallback(callbackData);

    // 模拟调用主线程回调
    // 实际实现中，这部分逻辑应该由原生层处理
    const { worker_id } = callbackData;

    // 查找对应的 worker 实例并调用回调
    // 实际实现中，这部分逻辑应该由原生层处理
  }
};

NativeWorkerService.__on__ = function (data) {
  const { msg: eventName, callback_id, worker_id } = data;

  // 生成消息唯一ID，用于防重复处理
  const messageId = `${eventName}_${callback_id || 'no_callback'}_${worker_id}_${Date.now()}`;

  // 检查消息是否已经处理过
  if (MESSAGE_PROCESSED_SET.has(messageId)) {
    console.warn(`Message already processed: ${messageId}, skipping...`);
    return;
  }

  // 标记消息为已处理
  MESSAGE_PROCESSED_SET.add(messageId);

  // 清理过期的消息ID（保留最近的1000个）
  if (MESSAGE_PROCESSED_SET.size > 1000) {
    const entries = Array.from(MESSAGE_PROCESSED_SET);
    entries.slice(0, 500).forEach((id) => MESSAGE_PROCESSED_SET.delete(id));
  }

  if (WORKER_CALLBACK_MAP[eventName]) {
    WORKER_CALLBACK_MAP[eventName](data.data, (params) => {
      // emit 可能没有传入callback
      if (callback_id) {
        NativeWorkerService.__serviceInvokeCallback__({
          callback_id,
          worker_id,
          data: params,
        });
      }
    });
  }
};

const Worker: IWorker = {
  sharedWorker: errorFn,
  on: errorFn,
  off: errorFn,
  emit: errorFn,
};

Worker.on = function (eventName: string, callback: () => void) {
  WORKER_CALLBACK_MAP[eventName] = callback;
};
Worker.off = function (eventName: string) {
  delete WORKER_CALLBACK_MAP[eventName];
};
Worker.emit = function (eventName: string, data: object) {
  return NativeWorkerService.__emit__(eventName, data);
};

Worker.sharedWorker = (bundleName: string) => {
  const map = SharedWorker.sharedWorkerInstanceMap;
  if (!map.has(bundleName)) map.set(bundleName, new SharedWorker(bundleName));
  return map.get(bundleName);
};

export default Worker;
